<template>
  <div class="p2p-stack" style="gap:32px">
    <section class="p2p-hero" aria-labelledby="source-title">
      <div class="p2p-stack" style="align-items:center;gap:14px;max-width:660px">
        <span class="p2p-chip mono">No setup · no brand questionnaire</span>
        <h1 id="source-title" class="p2p-h1 xl">What are you promoting?</h1>
        <p class="p2p-lead">Paste your website. We read it, learn what your product does, and draft posts you can publish today.</p>
      </div>
      <form class="p2p-urlbar" @submit.prevent="read">
        <label for="site-url" class="sr-only">Website address</label>
        <input id="site-url" v-model="url" type="text" inputmode="url" autocomplete="url" placeholder="yourproduct.com">
        <button type="submit" class="p2p-btn lg" :disabled="busy || waiting || (!url.trim() && notes.trim().length < 20 && !spokenText)">
          {{ busy ? 'Starting…' : waiting ? 'Waiting for your video…' : project && project.knowledge_ready ? 'Read again' : 'Read my site' }}
          <v-icon small color="#0b0b10">mdi-arrow-right</v-icon>
        </button>
      </form>
      <div class="p2p-row p2p-faint p2p-mono" style="justify-content:center;gap:10px 24px">
        <span><v-icon x-small color="#8a8a99">mdi-file-outline</v-icon> Homepage plus up to 2 linked pages</span>
        <span><v-icon x-small color="#8a8a99">mdi-clock-outline</v-icon> About 30 seconds</span>
        <span><v-icon x-small color="#8a8a99">mdi-shield-check-outline</v-icon> Nothing is posted until you say so</span>
      </div>
    </section>

    <section class="p2p-stack" aria-labelledby="more-title">
      <div class="p2p-between">
        <h2 id="more-title" class="p2p-h2">Add more <span class="p2p-faint" style="font-family:var(--font-body)">optional</span></h2>
        <p class="p2p-muted" style="margin:0">Real screenshots and footage make better posts than stock visuals.</p>
      </div>
      <input ref="images" type="file" accept="image/png,image/jpeg,image/webp" multiple hidden @change="upload($event, 'image')">
      <input ref="videos" type="file" accept="video/mp4,video/quicktime,video/webm" hidden @change="upload($event, 'video')">
      <div class="p2p-grid" style="grid-template-columns:repeat(auto-fit,minmax(min(210px,100%),1fr))">
        <button type="button" class="p2p-tile" :disabled="!member" @click="$refs.images.click()">
          <span class="p2p-tile-icon"><v-icon small>mdi-image-outline</v-icon></span>
          <span><span class="p2p-h3">Screenshots or images</span><br><span class="p2p-faint">PNG, JPG or WebP, up to 20 MB each</span></span>
        </button>
        <button type="button" class="p2p-tile" :disabled="!member" @click="$refs.videos.click()">
          <span class="p2p-tile-icon"><v-icon small>mdi-video-outline</v-icon></span>
          <span><span class="p2p-h3">A demo video</span><br><span class="p2p-faint">MP4, MOV or WebM, up to 100 MB</span></span>
        </button>
        <button type="button" class="p2p-tile" @click="showPages = true">
          <span class="p2p-tile-icon"><v-icon small>mdi-file-document-outline</v-icon></span>
          <span><span class="p2p-h3">Another page</span><br><span class="p2p-faint">Docs, a changelog or a launch post</span></span>
        </button>
        <button type="button" class="p2p-tile" @click="showNotes = true">
          <span class="p2p-tile-icon"><v-icon small>mdi-message-text-outline</v-icon></span>
          <span><span class="p2p-h3">Notes for the AI</span><br><span class="p2p-faint">What to mention, or describe a product without a site</span></span>
        </button>
      </div>
      <div v-if="!member" class="p2p-alert is-info">Create an account to add your own images and videos.</div>
      <div v-if="showPages" class="p2p-card">
        <label v-for="(page, i) in pages" :key="i" class="p2p-label">Extra page {{ i + 1 }}
          <input v-model="pages[i]" class="p2p-input" placeholder="yourproduct.com/changelog">
        </label>
      </div>
      <div v-if="showNotes || notes" class="p2p-card">
        <label class="p2p-label">Notes for the AI
          <textarea v-model="notes" class="p2p-textarea" maxlength="4000"
                    placeholder="What should the posts mention? Who is it for? Anything to avoid? You can also describe a product that has no website yet."></textarea>
        </label>
      </div>
      <div v-if="uploads.length" class="p2p-grid" style="grid-template-columns:repeat(auto-fill,minmax(150px,1fr))">
        <div v-for="item in uploads" :key="item.id" class="p2p-card tight">
          <MediaImg v-if="item.media" :src="item.media.poster_url || item.media.url" ratio="4 / 3" />
          <div v-else class="p2p-skeleton" style="aspect-ratio:4/3"></div>
          <div class="p2p-faint" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ item.filename }}</div>
          <div class="p2p-between">
            <span :class="['p2p-chip', item.status === 'complete' ? 'good' : item.status === 'failed' ? 'bad' : '']">{{ uploadLabel(item) }}</span>
            <button type="button" class="p2p-icon-btn" aria-label="Remove upload" @click="removeUpload(item)"><v-icon small>mdi-close</v-icon></button>
          </div>
          <div v-if="item.error" class="p2p-faint p2p-bad">{{ item.error }}</div>
        </div>
      </div>
      <div v-if="spoken.length" class="p2p-stack" style="gap:10px" aria-labelledby="spoken-title">
        <div class="p2p-between">
          <h3 id="spoken-title" class="p2p-h3">What your videos say</h3>
          <span class="p2p-faint">Used like your notes when we read your product.</span>
        </div>
        <div v-for="item in spoken" :key="'s' + item.id" class="p2p-card tight">
          <div class="p2p-between" style="gap:8px">
            <span class="p2p-faint" style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;min-width:0">{{ item.filename }}</span>
            <span :class="['p2p-chip', speechTone(item.speech)]">{{ speechLabel(item.speech) }}</span>
          </div>
          <p v-if="item.speech.text" class="p2p-text" style="margin:0;white-space:pre-wrap">{{ item.speech.text }}</p>
          <p v-else-if="item.speech.state === 'done'" class="p2p-faint" style="margin:0">{{ item.speech.edited ? 'You chose not to use what this video says.' : 'We did not hear anyone speak in this video.' }}</p>
          <p v-else-if="item.speech.state === 'failed'" class="p2p-faint p2p-bad" style="margin:0">{{ item.speech.error || 'We could not read what this video says.' }}</p>
          <div class="p2p-row" style="gap:8px">
            <button v-if="item.speech.state === 'unknown' || item.speech.state === 'failed'" type="button" class="p2p-btn sm soft" :disabled="item.speech.state === 'listening'" @click="transcribe(item)">{{ item.speech.state === 'failed' ? 'Try again' : 'Read what it says' }}</button>
            <button v-if="item.speech.state !== 'listening'" type="button" class="p2p-btn sm ghost" @click="openEdit(item)">{{ item.speech.text ? 'Edit' : 'Type what it says' }}</button>
            <button v-if="item.speech.text" type="button" class="p2p-btn sm ghost" @click="saveTranscript(item, '')">Don't use it</button>
            <button v-if="item.speech.edited && item.speech.transcript" type="button" class="p2p-btn sm ghost" @click="saveTranscript(item, null)">Use what we heard</button>
          </div>
        </div>
      </div>
    </section>

    <v-dialog :value="!!editing" max-width="640" @input="editing = null">
      <div v-if="editing" class="p2p-card raised">
        <h2 class="p2p-h2">What this video says</h2>
        <p class="p2p-muted" style="margin:0">Fix names or anything we misheard. We use this text like your notes; it is never posted as is.</p>
        <label class="p2p-label">{{ editing.filename }}
          <textarea v-model="editText" class="p2p-textarea" rows="8" maxlength="6000"></textarea>
        </label>
        <div class="p2p-row">
          <button type="button" class="p2p-btn" :disabled="saving || !editText.trim()" @click="saveTranscript(editing, editText)">Save</button>
          <button type="button" class="p2p-btn ghost" @click="editing = null">Cancel</button>
        </div>
      </div>
    </v-dialog>

    <div v-if="project && project.knowledge_ready" class="p2p-sticky">
      <span class="p2p-muted">We already know {{ project.name }}. Read again only if your site changed.</span>
      <button type="button" class="p2p-btn" @click="$emit('go', 'formats')">Continue <v-icon small color="#0b0b10">mdi-arrow-right</v-icon></button>
    </div>
  </div>
</template>

<script>
import MediaImg from '../MediaImg.vue';
import { get, post, patch, remove, uploadForm, message } from '../../api';
import { poller } from '../../poll';

export default {
  name: 'StepSource',
  components: { MediaImg },
  props: { project: { type: Object, default: null }, member: { type: Boolean, default: false } },
  data: () => ({ url: '', notes: '', pages: ['', ''], showPages: false, showNotes: false, busy: false, uploads: [], loop: null,
    editing: null, editText: '', saving: false, asked: {} }),
  computed: {
    spoken() {
      return this.uploads.filter(item => item.kind === 'video' && item.speech &&
        ['done', 'listening', 'failed', 'unknown'].indexOf(item.speech.state) >= 0);
    },
    spokenText() { return this.uploads.some(item => item.speech && item.speech.text); },
    // Reading the product waits for uploads still being checked and videos still being transcribed.
    waiting() {
      return this.uploads.some(item => (item.status !== 'complete' && item.status !== 'failed') ||
        (item.speech && (item.speech.state === 'listening' || (item.speech.state === 'available' && !this.asked[item.id]))));
    }
  },
  watch: {
    project: { immediate: true, handler(value) {
      if (value && value.primary_url && !this.url) this.url = value.primary_url.replace(/^https?:\/\//, '').replace(/\/$/, '');
      if (value && value.sources) {
        const note = value.sources.find(source => source.kind === 'note');
        if (note && !this.notes) this.notes = note.note || '';
        this.uploads = value.sources.filter(source => source.kind === 'image' || source.kind === 'video');
      }
    } }
  },
  beforeDestroy() { if (this.loop) this.loop.stop(); },
  methods: {
    async ensureProject() {
      if (this.project && this.project.id) return this.project;
      const created = await post('/projects', { url: this.url.trim(), notes: this.notes.trim() });
      this.$emit('project', created);
      this.$router.replace('/create/' + created.id + '/source').catch(() => {});
      return created;
    },
    async read() {
      this.busy = true;
      this.$emit('error', '');
      try {
        const project = await this.ensureProject();
        await post('/projects/' + project.id + '/ingest', { url: this.url.trim(), notes: this.notes.trim(),
          extra_urls: this.pages.map(page => page.trim()).filter(Boolean) });
        this.$emit('go', 'formats');
      } catch (error) {
        this.$emit('error', message(error));
      } finally {
        this.busy = false;
      }
    },
    uploadLabel(item) {
      if (item.status === 'complete') return item.kind === 'video' ? 'Video ready' : 'Image ready';
      if (item.status === 'failed') return 'Could not use it';
      return 'Checking…';
    },
    speechLabel(speech) {
      if (speech.state === 'listening') return 'Listening…';
      if (speech.state === 'failed') return 'Not read';
      if (speech.state === 'unknown') return 'Not read yet';
      if (speech.edited) return speech.text ? 'Your words' : 'Not used';
      return speech.text ? 'Heard' : 'No speech';
    },
    speechTone(speech) {
      if (speech.state === 'failed') return 'bad';
      return speech.state === 'done' && speech.text ? 'good' : '';
    },
    replace(fresh) {
      const index = this.uploads.findIndex(row => row.id === fresh.id);
      if (index >= 0) this.$set(this.uploads, index, fresh);
    },
    async transcribe(item, polling) {
      this.$set(this.asked, item.id, true);
      this.$emit('error', '');
      try {
        this.replace(await post('/sources/' + item.id + '/transcribe'));
        if (!polling) this.watchUploads();
      } catch (error) {
        this.$emit('error', message(error));
      }
    },
    openEdit(item) {
      this.editing = item;
      this.editText = item.speech.text || item.speech.transcript || '';
    },
    async saveTranscript(item, text) {
      this.saving = true;
      this.$emit('error', '');
      try {
        this.replace(await patch('/sources/' + item.id + '/transcript', { text: text === null ? null : String(text) }));
        this.editing = null;
      } catch (error) {
        this.$emit('error', message(error));
      } finally {
        this.saving = false;
      }
    },
    async upload(event, kind) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';
      if (!files.length) return;
      this.$emit('error', '');
      try {
        const project = await this.ensureProject();
        for (const file of files) {
          const limit = kind === 'video' ? 100 : 20;
          if (file.size > limit * 1024 * 1024) { this.$emit('error', file.name + ' is larger than ' + limit + ' MB.'); continue; }
          const form = new FormData();
          form.append('file', file);
          const item = await uploadForm('/projects/' + project.id + '/uploads', form);
          this.uploads.push(item);
        }
        this.watchUploads();
      } catch (error) {
        this.$emit('error', message(error));
      }
    },
    watchUploads() {
      if (this.loop) this.loop.stop();
      this.loop = poller(async () => {
        // A video with sound is transcribed as soon as it is ready (free); the member can edit or drop the words.
        for (const item of this.uploads.filter(row => row.speech && row.speech.state === 'available' && !this.asked[row.id])) {
          await this.transcribe(item, true);
        }
        const pending = this.uploads.filter(item => (item.status !== 'complete' && item.status !== 'failed') ||
          (item.speech && item.speech.state === 'listening'));
        if (!pending.length) return false;
        for (const item of pending) {
          this.replace(await get('/sources/' + item.id));
        }
        return true;
      });
      this.loop.start();
    },
    async removeUpload(item) {
      try {
        await remove('/sources/' + item.id);
        this.uploads = this.uploads.filter(row => row.id !== item.id);
      } catch (error) {
        this.$emit('error', message(error));
      }
    }
  },
  mounted() {
    // A link brought along from the landing page, sign-up or checkout (/create?url=).
    const linked = this.$route.query.url;
    if (!this.url && !(this.project && this.project.id) && typeof linked === 'string') this.url = linked.trim().slice(0, 2048);
    if (this.waiting) this.watchUploads();
  }
};
</script>

<style>
.sr-only{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.p2p-tile:disabled{opacity:.5;cursor:not-allowed}
</style>
